import {Observable, of} from "rxjs";
import {map, switchMap} from "rxjs/operators";
import {MvsCoreService} from "../../../core/shared/service/mvs-core.service";
import {MvsCrudService} from "../../../core/shared/service/crud/mvs-crud.service";
import {DtoDetail} from "../../../core/shared/dto/dto.detail";
import {DtoList} from "../../../core/shared/dto/dto.list";
import {
    CiInsurableObjectAddressFieldService
} from "../service/domain/ci-insurable-object-address-field.service";
import {CiInsurableObjectAddressFieldInfo} from "./ci-insurable-object-address-field";
import {
    CI_OBJECT_TYPE_CUSTOMER_ADDRESS,
    CiCustomerAddressRequest,
    CiCustomerAddressRow
} from "./ci-customer-address-request";
import {CiCustomerAddressPredicate} from "./ci-customer-address-predicate";

/**
 * The address situation of ONE insurable object, as far as the conditional "Adresse" tab needs it.
 */
export interface CiInsurableObjectAddress {

    /** The ad.Address field of the object's type, own or inherited; null = the type has none. */
    addressField: CiInsurableObjectAddressFieldInfo;

    /** The ad.Address the object points at; null = the type has a field but no value is set. */
    addressId: number;

    /** The ACTIVE addresses of the bound customer - the set the predicate compares against. */
    customerAddresses: CiCustomerAddressRow[];

    /**
     * The tab condition: the object HAS an address and that address is NOT one of the customer's.
     * See CiCustomerAddressPredicate for why this is not simply the negation.
     */
    objectSpecific: boolean;
}

/**
 * Resolve whether an insurable object carries an OBJECT SPECIFIC address (design section 15.2).
 *
 * Both object base components - the one for ci.InsurableObject and the one for
 * cr.CustomerInsurableObject - show their editable "Adresse" tab under exactly this condition, so
 * the three steps it takes (which field, which value, whose address) exist once here instead of
 * twice.
 *
 * Composed from the existing pieces rather than adding a data source of its own:
 * CiInsurableObjectAddressFieldService answers WHICH field (ancestor inheritance included),
 * CiCustomerAddressRequest loads the customer's addresses, CiCustomerAddressPredicate compares.
 * The services are passed in - like CiCustomerInsurableObjectAssignment - so the class stays
 * stateless and no caller can interfere with another.
 */
export class CiInsurableObjectAddressResolver {

    /**
     * @param addressFieldService resolves the ad.Address field of the object's type
     * @param coreService         used to resolve the cr.CustomerAddress service by alias (GC1)
     * @param insurableObject     the loaded ci.InsurableObject; typeDtoId and the generic
     *                            gen_<typeFieldId>DtoId value are read off it
     * @param customerId          the customer the object is bound to; null = none known, which
     *                            makes every address object specific (there is no customer address
     *                            set it could belong to)
     */
    static resolve(
        addressFieldService: CiInsurableObjectAddressFieldService,
        coreService: MvsCoreService,
        insurableObject: DtoDetail,
        customerId: number): Observable<CiInsurableObjectAddress> {

        const typeId = insurableObject?.['typeDtoId'];

        if (typeId == null) {
            return of(CiInsurableObjectAddressResolver.createEmpty());
        }

        return addressFieldService.getAddressField(typeId).pipe(
            switchMap((addressField: CiInsurableObjectAddressFieldInfo) => {

                if (!addressField) {
                    return of(CiInsurableObjectAddressResolver.createEmpty());
                }

                const addressId = insurableObject[addressField.valueIdKey] ?? null;

                if (addressId == null) {
                    // The type defines the field, this object simply has no value in it.
                    return of({
                        addressField: addressField,
                        addressId: null,
                        customerAddresses: [],
                        objectSpecific: false
                    });
                }

                return CiInsurableObjectAddressResolver.loadCustomerAddresses(coreService, customerId).pipe(
                    map((customerAddresses: CiCustomerAddressRow[]) => ({
                        addressField: addressField,
                        addressId: addressId,
                        customerAddresses: customerAddresses,
                        objectSpecific: CiCustomerAddressPredicate.isObjectSpecificAddress(
                            addressId, customerAddresses)
                    }))
                );
            })
        );
    }

    /**
     * The ACTIVE addresses of the customer, or an empty set when no customer is known.
     *
     * An empty set is not an error: the predicate then reports the object's address as object
     * specific, which is the correct reading - an object without a bound customer cannot be
     * pointing at "the customer's" address.
     */
    protected static loadCustomerAddresses(
        coreService: MvsCoreService, customerId: number): Observable<CiCustomerAddressRow[]> {

        if (!customerId) {
            return of([]);
        }

        const service: MvsCrudService = coreService?.getCrudService(CI_OBJECT_TYPE_CUSTOMER_ADDRESS);

        if (!service) {
            return of([]);
        }

        return service.list(CiCustomerAddressRequest.createActiveForCustomer(customerId)).pipe(
            map((dtoList: DtoList) => CiCustomerAddressRequest.mapRows(dtoList))
        );
    }

    /** No type, no field - nothing to show. */
    protected static createEmpty(): CiInsurableObjectAddress {

        return {
            addressField: null,
            addressId: null,
            customerAddresses: [],
            objectSpecific: false
        };
    }

}
